import {
  ElementType,
  LibraryElement,
  LibraryService,
  PublicComponent,
  SlotType,
} from 'base/library_service';
import { Component } from 'base/webflow/schema';
import { observable } from 'mobx';

export type CacheState<T> = { promise: Promise<T> } & (
  | {
      type: 'fulfilled';
      value: T;
    }
  | {
      type: 'pending';
      value?: undefined;
      promise: Promise<T>;
    }
  | {
      type: 'rejected';
      error: unknown;
      value?: undefined;
    }
);

export function addPromiseToCache<T>(
  cache: Map<string, CacheState<T>>,
  key: string,
  promise: Promise<T>,
): CacheState<T> {
  cache.set(key, { type: 'pending', promise });
  promise.then((value) => {
    cache.set(key, {
      type: 'fulfilled',
      value,
      promise,
    });
  });
  promise.catch((error) =>
    cache.set(key, {
      type: 'rejected',
      error,
      promise,
    }),
  );
  // We have to get after we set, so that mobx tracks the new key as being observed
  return cache.get(key)!;
}

export interface LibraryCache extends LibraryService {
  getCachedPublicComponent(
    slug: string,
    opts?: { fetchIfMissing?: boolean },
  ): CacheState<PublicComponent> | undefined;

  getCachedPublicComponentJson(
    slug: string,
    opts?: { fetchIfMissing?: boolean },
  ): CacheState<Component> | undefined;

  getCachedSlotType(
    id: string,
    opts?: { fetchIfMissing?: boolean },
  ): CacheState<SlotType> | undefined;

  getCachedElementType(
    id: string,
    opts?: { fetchIfMissing?: boolean },
  ): CacheState<ElementType> | undefined;

  getCachedLibraryElement(
    id: string,
    opts?: { fetchIfMissing?: boolean },
  ): CacheState<LibraryElement> | undefined;

  getCachedElementTypes(opts?: {
    fetchIfMissing?: boolean;
  }): CacheState<ElementType[]> | undefined;
}

export class LibraryCacheImpl implements LibraryCache {
  private readonly jsonCache = observable.map<string, CacheState<Component>>(
    [],
    { deep: false },
  );
  private readonly componentCache = observable.map<
    string,
    CacheState<PublicComponent>
  >([], { deep: false });
  private readonly slotTypeCache = observable.map<string, CacheState<SlotType>>(
    [],
    { deep: false },
  );
  private readonly elementTypeCache = observable.map<
    string,
    CacheState<ElementType>
  >([], { deep: false });
  private readonly libraryElementCache = observable.map<
    string,
    CacheState<LibraryElement>
  >([], { deep: false });

  // TODO: This is a hack to extend the existing cache setup to support list
  // endpoints... it should be merged with the single item cache.
  private readonly elementTypesCache = observable.map<
    string,
    CacheState<ElementType[]>
  >([], { deep: false });

  constructor(private readonly service: LibraryService) {}

  private addPublicComponentToCache(component: PublicComponent) {
    this.componentCache.set(component.componentSlug, {
      type: 'fulfilled',
      value: component,
      promise: Promise.resolve(component),
    });
  }

  getCachedPublicComponent(slug: string, { fetchIfMissing = false } = {}) {
    const v = this.componentCache.get(slug);
    if (v == null && fetchIfMissing) {
      const promise = this.findPublicComponents({
        filterComponentSlug: [slug],
      }).then((res) => {
        if (res.components.length === 0) {
          throw new Error(`Component ${slug} not found`);
        }
        return res.components[0];
      });
      return addPromiseToCache(this.componentCache, slug, promise);
    }
    return v;
  }

  getCachedPublicComponentJson(slug: string, { fetchIfMissing = false } = {}) {
    const cacheKey = `public:${slug}`;
    const v = this.jsonCache.get(cacheKey);
    if (v == null && fetchIfMissing) {
      return addPromiseToCache(
        this.jsonCache,
        cacheKey,
        this.service.getPublicComponentJson(slug),
      );
    }
    return v;
  }

  async getPublicComponentJson(
    slug: string,
    opts?: { bypassCloudfront?: boolean },
  ): Promise<Component> {
    if (opts?.bypassCloudfront) {
      return this.service.getPublicComponentJson(slug, opts);
    }
    return this.getCachedPublicComponentJson(slug, {
      fetchIfMissing: true,
    })!.promise;
  }

  async findPublicComponents(
    ...args: Parameters<LibraryService['findPublicComponents']>
  ) {
    const res = await this.service.findPublicComponents(...args);
    res.components.forEach((c) => this.addPublicComponentToCache(c));
    return res;
  }

  async getSlotType(id: string) {
    return this.getCachedSlotType(id, { fetchIfMissing: true })!.promise;
  }

  getCachedSlotType(id: string, { fetchIfMissing = false } = {}) {
    const v = this.slotTypeCache.get(id);
    if (v == null && fetchIfMissing) {
      return addPromiseToCache(
        this.slotTypeCache,
        id,
        this.service.getSlotType(id),
      );
    }
    return v;
  }

  async getElementType(id: string) {
    return this.getCachedElementType(id, { fetchIfMissing: true })!.promise;
  }

  getCachedElementType(id: string, { fetchIfMissing = false } = {}) {
    const v = this.elementTypeCache.get(id);
    if (v == null && fetchIfMissing) {
      return addPromiseToCache(
        this.elementTypeCache,
        id,
        this.service.getElementType(id),
      );
    }
    return v;
  }

  async getLibraryElement(id: string) {
    return this.getCachedLibraryElement(id, { fetchIfMissing: true })!.promise;
  }

  getCachedLibraryElement(id: string, { fetchIfMissing = false } = {}) {
    const v = this.libraryElementCache.get(id);
    if (v == null && fetchIfMissing) {
      return addPromiseToCache(
        this.libraryElementCache,
        id,
        this.service.getLibraryElement(id),
      );
    }
    return v;
  }

  listSlotTypes() {
    return this.service.listSlotTypes();
  }

  listElementTypes() {
    return this.service.listElementTypes();
  }

  listLibraryElements() {
    return this.service.listLibraryElements();
  }

  getCachedElementTypes({ fetchIfMissing = false } = {}):
    | CacheState<ElementType[]>
    | undefined {
    const v = this.elementTypesCache.get('');
    if (v == null && fetchIfMissing) {
      return addPromiseToCache(
        this.elementTypesCache,
        '',
        this.service
          .listElementTypes()
          .then(({ elementTypes }) => elementTypes),
      );
    }
    return v;
  }

  copyPublicComponent(
    ...args: Parameters<LibraryService['copyPublicComponent']>
  ) {
    return this.service.copyPublicComponent(...args);
  }

  findPublicCategories(
    ...args: Parameters<LibraryService['findPublicCategories']>
  ) {
    return this.service.findPublicCategories(...args);
  }

  findBlocksCategories() {
    return this.service.findBlocksCategories();
  }

  getHostedFolder(...args: Parameters<LibraryService['getHostedFolder']>) {
    return this.service.getHostedFolder(...args);
  }

  findRecentlyUsedHostedComponents(
    ...args: Parameters<LibraryService['findRecentlyUsedHostedComponents']>
  ) {
    return this.service.findRecentlyUsedHostedComponents(...args);
  }

  searchHostedLibrary(
    ...args: Parameters<LibraryService['searchHostedLibrary']>
  ) {
    return this.service.searchHostedLibrary(...args);
  }

  copyHostedComponent(
    ...args: Parameters<LibraryService['copyHostedComponent']>
  ) {
    return this.service.copyHostedComponent(...args);
  }
}
